import { auth } from "src/util/firebase";
import { onAuthStateChanged, User } from "firebase/auth";
import { useEffect, useState, useContext, createContext } from "react";
import { flushSync } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import { setAuthorizedUser } from "src/util/axios_init";
import LoadingOverlay from "./LoadingOverlay";

export interface AuthContext {
  user: User | null;
}

declare global {
  interface Window {
    $crisp: any[];
  }
}

const AuthContext = createContext<AuthContext | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refreshToken = urlParams.get("r") === "1";
    const authorizedUserId = urlParams.get("u");

    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      try {
        if (refreshToken && authUser) {
          await authUser.getIdToken(true);
        } else if (authUser) {
          await authUser.getIdToken();
        }

        // Set authorized user if specified in URL
        if (authorizedUserId) {
          setAuthorizedUser(authorizedUserId);
        }

        // Flush the Dom to ensure the auth state is updated before rendering the children
        // https://github.com/TanStack/router/discussions/1668#discussioncomment-9726727
        flushSync(() => {
          if (authUser) {
            setUser(authUser);
          } else {
            setUser(null);
            queryClient.clear();
          }
          setIsInitializing(false);
        });
      } catch (error) {
        console.error("Error during auth initialization:", error);
        setIsInitializing(false);
      }
    });

    return unsubscribe;
  }, []);

  if (isInitializing) {
    return <LoadingOverlay isLoading={true} classNames="h-screen w-screen" />;
  }

  return (
    <AuthContext.Provider value={{ user }}>{children}</AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
